import { Box, SxProps, Theme, Typography } from "@mui/material";
import {
  TaskState,
  TaskType,
  isTaskTypeChatConcern,
  isTaskTypeSelfTestConcern,
  isTaskTypeToFollowup,
} from "../features/data/patientApiSlice";
import { computeReadableValue, sanitizeToKey } from "../utils/strings";

import AiMismatchIcon from "../../assets/flag_ai_mismatch.svg?react";
import AlcoholIcon from "../../assets/flag_alcohol.svg?react";
import AnxietyIcon from "../../assets/flag_anxiety.svg?react";
import DepressionIcon from "../../assets/flag_depression.svg?react";
import HateHarassmentIcon from "../../assets/flag_hate_harassment.svg?react";
import HivVulnerabilityIcon from "../../assets/flag_hiv_vulnerability.svg?react";
import IllicitIcon from "../../assets/flag_illicit.svg?react";
import InterestInSelfTestIcon from "../../assets/flag_self_test_interest.svg?react";
import InvalidResultIcon from "../../assets/flag_invalid_result.svg?react";
import NegativeResultIcon from "../../assets/flag_negative_result.svg?react";
import NoAiInterpretationIcon from "../../assets/flag_no_ai_interpretation.svg?react";
import PositiveResultIcon from "../../assets/flag_positive_result.svg?react";
import PossibleInterestInPepOrPrepIcon from "../../assets/flag_pep_prep_possible_interest.svg?react";
import React from "react";
import RequestToChatIcon from "../../assets/flag_request_to_chat.svg?react";
import STITestIcon from "../../assets/flag_sti_test.svg?react";
import SelfHarmIcon from "../../assets/flag_selfharm.svg?react";
import SexWithMinorIcon from "../../assets/flag_sex_with_minor.svg?react";
import SexualAssaultIcon from "../../assets/flag_sexual_assault.svg?react";
import TBTestIcon from "../../assets/flag_tb_test.svg?react";
import UnknownIcon from "../../assets/flag_unknown.svg?react";
import ViolenceIcon from "../../assets/flag_violence.svg?react";
import { createStyleSheet } from "../utils/styles";
import i18n from "../../i18n";
import { theme } from "../../theme";

enum OpenAiConcernFlag {
  harassment = "harassment",
  harassment_threatening = "harassment_threatening",
  hate = "hate",
  hate_threatening = "hate_threatening",
  self_harm = "self_harm",
  self_harm_intent = "self_harm_intent",
  self_harm_instructions = "self_harm_instructions",
  sexual = "sexual",
  sexual_minors = "sexual_minors",
  violence = "violence",
  violence_graphic = "violence_graphic",
}

enum ConcernFlagType {
  hate_harassment = "hate_harassment",
  self_harm_suicide = "self_harm_suicide",
  explicit_content_sexual_assault = "explicit_content_sexual_assault",
  gbv_sexual_assault = "gbv_sexual_assault",
  sex_with_minor = "sex_with_minor",
  violence = "violence",
  ai_mismatch = "ai_mismatch",
  positive_result = "positive_result",
  negative_result = "negative_result",
  invalid_result = "invalid_result",
  no_ai_interpretation = "no_ai_interpretation",
  hiv_vulnerability = "hiv_vulnerability",
  self_reported_hiv_positive_status = "self_reported_hiv_positive_status",
  request_to_chat = "request_to_chat",
  possible_interest_in_prep_or_pep = "possible_interest_in_prep_or_pep",
  interest_in_self_testing = "interest_in_self_testing",
  celosia_concern_hiv_risk_score = "celosia_concern_hiv_risk_score",
  celosia_mental_health_further_screening_required = "celosia_mental_health_further_screening_required",
  celosia_potential_sti = "celosia_potential_sti",
  mangrove_alcohol_further_screening_required = "mangrove_alcohol_further_screening_required",
  mangrove_anxiety_further_screening_required = "mangrove_anxiety_further_screening_required",
  mangrove_depression_further_screening_required = "mangrove_depression_further_screening_required",
  mangrove_tb_vulnerable = "mangrove_tb_vulnerable",
  protea_alcohol_further_screening_required = "protea_alcohol_further_screening_required",
  protea_anxiety_further_screening_required = "protea_anxiety_further_screening_required",
  protea_depression_further_screening_required = "protea_depression_further_screening_required",
  illicit = "illicit",
  illicit_violent = "illicit_violent",
  unknown = "unknown",
}

const openAiConcernToConcernFlagTypeMap: {
  [key in OpenAiConcernFlag]?: ConcernFlagType;
} = {
  [OpenAiConcernFlag.harassment]: ConcernFlagType.hate_harassment,
  [OpenAiConcernFlag.harassment_threatening]: ConcernFlagType.hate_harassment,
  [OpenAiConcernFlag.hate]: ConcernFlagType.hate_harassment,
  [OpenAiConcernFlag.hate_threatening]: ConcernFlagType.hate_harassment,
  [OpenAiConcernFlag.self_harm]: ConcernFlagType.self_harm_suicide,
  [OpenAiConcernFlag.self_harm_intent]: ConcernFlagType.self_harm_suicide,
  [OpenAiConcernFlag.self_harm_instructions]: ConcernFlagType.self_harm_suicide,
  [OpenAiConcernFlag.sexual]: ConcernFlagType.explicit_content_sexual_assault,
  [OpenAiConcernFlag.sexual_minors]: ConcernFlagType.sex_with_minor,
  [OpenAiConcernFlag.violence]: ConcernFlagType.violence,
  [OpenAiConcernFlag.violence_graphic]: ConcernFlagType.violence,
};

const taskTypeToConcernFlagTypeMap: { [key in TaskType]?: ConcernFlagType } = {
  [TaskType.concern_interpretation_mismatch]: ConcernFlagType.ai_mismatch,
  [TaskType.concern_positive_test]: ConcernFlagType.positive_result,
  [TaskType.concern_negative_test]: ConcernFlagType.negative_result,
  [TaskType.concern_invalid_test]: ConcernFlagType.invalid_result,
  [TaskType.concern_risk_score]: ConcernFlagType.hiv_vulnerability,
  [TaskType.concern_risk_other]: ConcernFlagType.hiv_vulnerability,
  [TaskType.request_to_chat]: ConcernFlagType.request_to_chat,
  [TaskType.concern_self_reported_hiv_positive_status]:
    ConcernFlagType.self_reported_hiv_positive_status,
  [TaskType.possible_interest_in_prep_or_pep]:
    ConcernFlagType.possible_interest_in_prep_or_pep,
  [TaskType.interest_in_self_testing]: ConcernFlagType.interest_in_self_testing,
  [TaskType.celosia_concern_hiv_risk_score]:
    ConcernFlagType.celosia_concern_hiv_risk_score,
  [TaskType.celosia_mental_health_further_screening_required]:
    ConcernFlagType.celosia_mental_health_further_screening_required,
  [TaskType.celosia_potential_sti]: ConcernFlagType.celosia_potential_sti,
  [TaskType.mangrove_alcohol_further_screening_required]:
    ConcernFlagType.mangrove_alcohol_further_screening_required,
  [TaskType.mangrove_anxiety_further_screening_required]:
    ConcernFlagType.mangrove_anxiety_further_screening_required,
  [TaskType.mangrove_depression_further_screening_required]:
    ConcernFlagType.mangrove_depression_further_screening_required,
  [TaskType.mangrove_tb_vulnerable]: ConcernFlagType.mangrove_tb_vulnerable,
  [TaskType.protea_alcohol_further_screening_required]:
    ConcernFlagType.protea_alcohol_further_screening_required,
  [TaskType.protea_anxiety_further_screening_required]:
    ConcernFlagType.protea_anxiety_further_screening_required,
  [TaskType.protea_depression_further_screening_required]:
    ConcernFlagType.protea_depression_further_screening_required,
};

interface ConcernFlagTypeInfo {
  Icon: React.FC<
    React.SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  iconColor: string;
  setStrokeNotFill?: boolean;
  borderColor: string;
  backgroundColor: string;
}

const concernMap: { [key in ConcernFlagType]: ConcernFlagTypeInfo } = {
  [ConcernFlagType.hate_harassment]: {
    Icon: HateHarassmentIcon,
    iconColor: "#e68424",
    borderColor: "#f8c593",
    backgroundColor: "#fff3e8",
  },
  [ConcernFlagType.self_harm_suicide]: {
    Icon: SelfHarmIcon,
    iconColor: "#c37eb3",
    borderColor: "#e0b2d6",
    backgroundColor: "#ffeffb",
  },
  [ConcernFlagType.explicit_content_sexual_assault]: {
    Icon: SexualAssaultIcon,
    iconColor: "#58368e",
    borderColor: "#a392c8",
    backgroundColor: "#f2eefc",
  },
  [ConcernFlagType.gbv_sexual_assault]: {
    Icon: SexualAssaultIcon,
    iconColor: "#58368e",
    borderColor: "#a392c8",
    backgroundColor: "#f2eefc",
  },
  [ConcernFlagType.sex_with_minor]: {
    Icon: SexWithMinorIcon,
    iconColor: "#ec8282",
    borderColor: "#ebbfbf",
    backgroundColor: "#ffeded",
  },
  [ConcernFlagType.violence]: {
    Icon: ViolenceIcon,
    iconColor: "#ad3730",
    borderColor: "#e36b64",
    backgroundColor: "#ffe7e5",
  },
  [ConcernFlagType.ai_mismatch]: {
    Icon: AiMismatchIcon,
    iconColor: "#c59901",
    borderColor: "#f2d07a",
    backgroundColor: "#fff7e4",
  },
  [ConcernFlagType.positive_result]: {
    Icon: PositiveResultIcon,
    iconColor: "#c59901",
    borderColor: "#f2d07a",
    backgroundColor: "#fff7e4",
  },
  [ConcernFlagType.negative_result]: {
    Icon: NegativeResultIcon,
    iconColor: "#c59901",
    borderColor: "#f2d07a",
    backgroundColor: "#fff7e4",
  },
  [ConcernFlagType.invalid_result]: {
    Icon: InvalidResultIcon,
    iconColor: "#c59901",
    borderColor: "#f2d07a",
    backgroundColor: "#fff7e4",
  },
  [ConcernFlagType.no_ai_interpretation]: {
    Icon: NoAiInterpretationIcon,
    iconColor: "#c59901",
    borderColor: "#f2d07a",
    backgroundColor: "#fff7e4",
  },
  [ConcernFlagType.hiv_vulnerability]: {
    Icon: HivVulnerabilityIcon,
    iconColor: "#ea2726",
    borderColor: "#ea2626",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.self_reported_hiv_positive_status]: {
    Icon: PositiveResultIcon,
    iconColor: "#ea2726",
    borderColor: "#ea2626",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.request_to_chat]: {
    Icon: RequestToChatIcon,
    iconColor: "#c59901",
    borderColor: "#f2d07a",
    backgroundColor: "#fff7e4",
  },
  [ConcernFlagType.possible_interest_in_prep_or_pep]: {
    Icon: PossibleInterestInPepOrPrepIcon,
    iconColor: "#979041",
    borderColor: "#d7cf76",
    backgroundColor: "#fcfade",
  },
  [ConcernFlagType.interest_in_self_testing]: {
    Icon: InterestInSelfTestIcon,
    iconColor: "#dc7d38",
    setStrokeNotFill: true,
    borderColor: "#f8ad78",
    backgroundColor: "#feeee3",
  },
  [ConcernFlagType.celosia_concern_hiv_risk_score]: {
    Icon: HivVulnerabilityIcon,
    iconColor: "#ea2726",
    borderColor: "#ea2626",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.celosia_mental_health_further_screening_required]: {
    Icon: DepressionIcon,
    iconColor: "#dd9232",
    borderColor: "#ffb452",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.celosia_potential_sti]: {
    Icon: STITestIcon,
    iconColor: "#dd9232",
    borderColor: "#ffb452",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.mangrove_alcohol_further_screening_required]: {
    Icon: AlcoholIcon,
    iconColor: "#dd9232",
    borderColor: "#ffb452",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.mangrove_anxiety_further_screening_required]: {
    Icon: AnxietyIcon,
    iconColor: "#dd9232",
    borderColor: "#ffb452",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.mangrove_depression_further_screening_required]: {
    Icon: DepressionIcon,
    iconColor: "#dd9232",
    borderColor: "#ffb452",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.mangrove_tb_vulnerable]: {
    Icon: TBTestIcon,
    iconColor: "#dd9232",
    borderColor: "#ffb452",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.protea_alcohol_further_screening_required]: {
    Icon: AlcoholIcon,
    iconColor: "#dd9232",
    borderColor: "#ffb452",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.protea_anxiety_further_screening_required]: {
    Icon: AnxietyIcon,
    iconColor: "#dd9232",
    borderColor: "#ffb452",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.protea_depression_further_screening_required]: {
    Icon: DepressionIcon,
    iconColor: "#dd9232",
    borderColor: "#ffb452",
    backgroundColor: "#ffefec",
  },
  [ConcernFlagType.illicit]: {
    Icon: IllicitIcon,
    iconColor: "#55427f",
    borderColor: "#7e6ca4",
    backgroundColor: "#e6e0f4",
  },
  [ConcernFlagType.illicit_violent]: {
    Icon: IllicitIcon,
    iconColor: "#55427f",
    borderColor: "#7e6ca4",
    backgroundColor: "#e6e0f4",
  },
  [ConcernFlagType.unknown]: {
    Icon: UnknownIcon,
    iconColor: "#bfbfbf",
    borderColor: "#efefef",
    backgroundColor: "#ffffff",
  },
};

const computeConcernFlagType = (
  taskType: TaskType,
  concernFlagText: string | null,
  includeRequestToChat?: boolean
) =>
  isTaskTypeSelfTestConcern(taskType) ||
  isTaskTypeToFollowup(taskType) ||
  (!!includeRequestToChat && taskType === TaskType.request_to_chat)
    ? taskTypeToConcernFlagTypeMap[taskType] || ConcernFlagType.unknown
    : isTaskTypeChatConcern(taskType)
    ? openAiConcernToConcernFlagTypeMap[ // TODO: TEMPORARY - allow tasks with OpenAI flags to be mapped to our standardized ones
        sanitizeToKey(concernFlagText) as OpenAiConcernFlag
      ] ||
      (concernMap[sanitizeToKey(concernFlagText) as ConcernFlagType]
        ? (sanitizeToKey(concernFlagText) as ConcernFlagType)
        : ConcernFlagType.unknown)
    : ConcernFlagType.unknown;

const computeDescriptionText = (
  taskType: TaskType,
  concernFlagType: ConcernFlagType,
  concernFlagText: string | null,
  includeRequestToChat?: boolean
) => {
  return isTaskTypeSelfTestConcern(taskType) ||
    isTaskTypeToFollowup(taskType) ||
    (!!includeRequestToChat && taskType === TaskType.request_to_chat)
    ? i18n.t("taskTypes:" + taskType)
    : isTaskTypeChatConcern(taskType)
    ? concernFlagType !== ConcernFlagType.unknown
      ? i18n.t("concernFlags:" + concernFlagType)
      : computeReadableValue(concernFlagText || "")
    : undefined;
};

export const willConcernPillRender = (
  taskType: TaskType,
  concernFlagText: string | null,
  includeRequestToChat?: boolean
) => {
  const concernFlagType = computeConcernFlagType(
    taskType,
    concernFlagText,
    includeRequestToChat
  );
  return (
    (computeDescriptionText(
      taskType,
      concernFlagType,
      concernFlagText,
      includeRequestToChat
    )?.length || 0) > 0
  );
};

const ConcernPill = (props: {
  taskType: TaskType;
  taskState: TaskState;
  concernFlagText: string | null;
  iconOnly?: boolean;
  includeRequestToChat?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const {
    taskType,
    taskState,
    concernFlagText,
    iconOnly,
    includeRequestToChat,
    sx,
  } = props;
  const concernFlagType = computeConcernFlagType(
    taskType,
    concernFlagText,
    includeRequestToChat
  );
  const displayText = computeDescriptionText(
    taskType,
    concernFlagType,
    concernFlagText,
    includeRequestToChat
  );
  const concernFlagTypeInfo = concernMap[concernFlagType];
  const Icon = concernFlagTypeInfo?.Icon || UnknownIcon;
  const { iconColor, setStrokeNotFill, borderColor, backgroundColor } =
    taskState !== TaskState.todo || !concernFlagTypeInfo
      ? {
          iconColor: "#bfbfbf",
          setStrokeNotFill: concernFlagTypeInfo?.setStrokeNotFill || false,
          borderColor: "#efefef",
          backgroundColor: "#ffffff",
        }
      : concernFlagTypeInfo;

  return displayText ? (
    <Box
      sx={
        {
          ...styles.container,
          backgroundColor,
          border: `1px solid ${borderColor}`,
          ...sx,
        } as SxProps<Theme>
      }
    >
      <Icon
        {...(setStrokeNotFill && { stroke: iconColor })}
        {...(!setStrokeNotFill && { fill: iconColor })}
      />
      {!iconOnly && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={styles.concernText}
          noWrap
        >
          {displayText}
        </Typography>
      )}
    </Box>
  ) : null;
};

const styles = createStyleSheet({
  container: {
    borderRadius: 1000,
    height: 30,
    px: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  concernText: {
    marginLeft: 0.5,
    [theme.breakpoints.down("desktop")]: {
      display: "none",
    },
  },
  listContainer: {
    display: "flex",
    flexDirection: "row",
  },
});

export default ConcernPill;
